import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../../../services/api-base';
import type {
  AccessibleOrg,
  AdminPickerItem,
  CustomerOrgItem,
  EntityCheckoutInfo,
  SetUserContextParams,
} from '../../../types/org-selection.types';

/**
 * Wire → `AccessibleOrg` for the customer flow. Exported for its test; the
 * slice wires it as `transformResponse` so nothing downstream sees the wire
 * shape (spec §4.2 / D13).
 */
export const normalizeCustomerOrgList = (
  rows: CustomerOrgItem[],
): AccessibleOrg[] =>
  rows.map((row) => ({
    entityId: row.entityId,
    name: row.company,
    role: row.role,
    disabled: row.disabled === true,
    parentEntityUuid: row.parentEntityUuid,
    logo: row.logo,
  }));

/**
 * Wire → `AccessibleOrg` for the internal picker. Its rows carry no role: an
 * internal caller holds the same role in every account, so each row gets
 * `callerRoleSuffix` (`toRoleSuffix` of the session's role).
 *
 * Not a fixed `'InternalAdmin'` any more (DV-3337). That was right while only
 * an internal admin called this; post-processing users now do too, and the
 * row's role becomes the `groupName` suffix `switchEntity` sends. user-service
 * accepts any suffix from an internal caller, so a fixed admin suffix would
 * hand a post-processing session an INTERNAL_ADMIN context on its next switch.
 */
export const normalizeInternalPicker = (
  rows: AdminPickerItem[],
  callerRoleSuffix: string,
): AccessibleOrg[] =>
  rows.map((row) => ({
    entityId: row.entityId,
    name: row.entityName,
    role: callerRoleSuffix,
    disabled: false,
    parentEntityUuid: row.parentEntityUuid,
  }));

/**
 * Org-selection API surface — used during post-login org bootstrap, in-app
 * entity switching, and the admin org-selection page. Auth-adjacent: every
 * mutation here can land while the user has just acquired their first token,
 * so the slice runs on `baseQueryWithAuth` (same as orderApi/propertyApi) and
 * inherits the shared 401/403 → silent-refresh-then-retry → endSession flow.
 */
export const orgSelectionApi = createApi({
  reducerPath: 'orgSelectionApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    /**
     * POST /api/v1/users/context — Sets the active group context on the user
     * record. Called both at login (with `groupName = JWT.CurrentContext`) and
     * during in-app entity switch (with `groupName = company + role`
     * concatenated; backend's `getCompanyName(groupName)` re-splits it).
     * Both also send the account's `entityId` (DV-3027): user-service falls
     * back to it only when the name no longer resolves, i.e. after a rename.
     * Returns void.
     *
     * <p>`tokenOnly` (DV-3421): it acts on the caller's token, not on a
     * selection. That applies to BOTH callers. For the cross-tree switch it is
     * the point — the tab's selection may be one the token cannot reach. For
     * `CallBack`'s login replay it changes behaviour too: the replay used to
     * carry whatever selection this tab had persisted from an earlier session;
     * it now carries none, so the guard answers `NO_SELECTION` and user-service
     * authorises the replay on the token and the body's group alone — which is
     * what the replay asks for anyway.
     */
    setUserContext: builder.mutation<void, SetUserContextParams>({
      query: ({ groupName, userId, entityId }) => ({
        url:
          `/api/v1/users/context?groupName=${encodeURIComponent(groupName)}&userId=${encodeURIComponent(userId)}` +
          (entityId ? `&entityId=${encodeURIComponent(entityId)}` : ''),
        method: 'POST',
      }),
      extraOptions: { tokenOnly: true },
    }),

    /**
     * GET /entity-service/api/v1/entities/scope/check?entityId={id} — may this
     * token select that entity? DV-3421.
     *
     * <p>Answered with the guard's own rule — would it HONOUR that selection:
     * for a caller holding any of `guard.unrestricted-roles`, whether the
     * entity exists; otherwise whether it is in the token's root subtree; and
     * with the guard off or `honour-selection` off, only for the token's own
     * `EntityUUID`. Asks only about the CALLER's reach, so it discloses nothing
     * about the entity itself.
     */
    checkScope: builder.query<{ reachable: boolean }, string>({
      query: (entityId) => ({
        url: `/entity-service/api/v1/entities/scope/check?entityId=${encodeURIComponent(entityId)}`,
        method: 'GET',
      }),
      extraOptions: { tokenOnly: true },
    }),

    /**
     * GET /entity-service/api/v1/entities?entityId={id} — Returns the full
     * `Entity` document. The org-selection flow only consults `skipCheckout`,
     * so the response is narrowed accordingly.
     */
    getEntityInfo: builder.query<EntityCheckoutInfo[], string>({
      query: (entityId) =>
        `/entity-service/api/v1/entities?entityId=${encodeURIComponent(entityId)}`,
    }),

    /**
     * {@link getEntityInfo} for `switchEntity` only, with no `X-Entity-UUID`
     * (DV-3421 review round 6, item C). A switch reads the TARGET's document
     * by id while this tab's selection may be one the token no longer reaches
     * (a pre-stamp session, or a tab waiting to reload after another tab moved
     * the token); sending that selection got the read refused, so a user
     * picking their own home company failed at `'entity-info'`. A separate
     * endpoint so the page-level readers of `getEntityInfo` keep their header.
     */
    getEntityInfoForSwitch: builder.query<EntityCheckoutInfo[], string>({
      query: (entityId) =>
        `/entity-service/api/v1/entities?entityId=${encodeURIComponent(entityId)}`,
      extraOptions: { tokenOnly: true },
    }),

    /**
     * GET /api/v1/entities/{userId} — Returns the user's accessible orgs as
     * `EntityDetails` projections (one per Keycloak customer group). Used by
     * the customer-flow `OrganizationSelectionPage` and the post-login bootstrap.
     */
    getCustomerOrgList: builder.query<AccessibleOrg[], string>({
      query: (userId) => `/api/v1/entities/${encodeURIComponent(userId)}`,
      transformResponse: normalizeCustomerOrgList,
    }),


    /**
     * GET /entity-service/api/v1/entities/picker — the login path's org list
     * (DV-2958). One projected query on `entities`: no order-service or
     * property-service fan-out, so it does not carry the ~35 s that
     * `companyListView=true` pays before anything paints.
     *
     * Used by every consumer that needs "which accounts exist" — the post-login
     * bootstrap (`CallBack`, `Loader`), the org switcher, and
     * `resolveEntityScope`. The Accounts Console gets its counts, PMS source and
     * origin from the account-view projection endpoint instead.
     *
     * `companyListView=true` has no frontend caller left; the endpoint itself is
     * retired on the entity-service side in a follow-up, after both count sources
     * have been diffed in an environment.
     */
    /** The arg is the caller's role suffix, stamped on every row — see `normalizeInternalPicker`. */
    getInternalOrgPicker: builder.query<AccessibleOrg[], string>({
      query: () => '/entity-service/api/v1/entities/picker',
      transformResponse: (rows: AdminPickerItem[], _meta, callerRoleSuffix) =>
        normalizeInternalPicker(rows, callerRoleSuffix),
    }),
  }),
});

export const {
  useSetUserContextMutation,
  useGetEntityInfoQuery,
  useLazyGetEntityInfoQuery,
  useGetCustomerOrgListQuery,
  useLazyGetCustomerOrgListQuery,
  useGetInternalOrgPickerQuery,
  useLazyGetInternalOrgPickerQuery,
} = orgSelectionApi;
